import type {
  CartProductItem,
  ProductItemInput,
  ProductSelection,
} from "../types/product-item.type";

// Products of one diet position, as the cart returned them.
export const productItemsForItem = (
  productItems: CartProductItem[] | undefined,
  configurationItemId: string | undefined,
): CartProductItem[] =>
  configurationItemId
    ? (productItems ?? []).filter(
        ({ productItem }) =>
          productItem.configurationItemId === configurationItemId,
      )
    : [];

const toInput = ({ productItem }: CartProductItem): ProductItemInput => ({
  id: productItem.id,
  configurationItemId: productItem.configurationItemId,
  merchantId: productItem.merchantId,
  productId: productItem.productId,
  date: productItem.date,
  quantity: productItem.quantity,
});

// Pass the products the cart already holds back through PUT unchanged. Needed
// by every write that is not about products, because the backend deletes
// whatever the payload leaves out.
export const toProductItemInputs = (
  productItems: CartProductItem[] | undefined,
): ProductItemInput[] => (productItems ?? []).map(toInput);

// Products for a PUT that drops a diet position: its products go with it (the
// backend rejects a product without its parent as orphaned). Works on the PUT
// shape so a base rebuilt from the client's copy of the cart can use it too.
export const productItemsWithoutItem = (
  productItems: ProductItemInput[],
  configurationItemId: string,
): ProductItemInput[] =>
  productItems.filter(
    (productItem) => productItem.configurationItemId !== configurationItemId,
  );

// Products for a PUT that rewrites one diet position's selection: the other
// positions keep theirs, the position gets `selections`. Entries that are
// already in the cart keep their id so the backend updates them in place.
// Without `selections` the position's products are left as they are.
export const replaceItemProductItems = (
  productItems: ProductItemInput[],
  configurationItemId: string,
  selections?: ProductSelection[],
): ProductItemInput[] => {
  if (!selections) {
    return productItems;
  }

  const idByKey = new Map(
    productItems
      .filter(
        (productItem) =>
          productItem.configurationItemId === configurationItemId &&
          productItem.id,
      )
      .map((productItem) => [
        `${productItem.productId}|${productItem.date}`,
        productItem.id,
      ]),
  );

  return [
    ...productItemsWithoutItem(productItems, configurationItemId),
    ...selections.map((selection) => {
      const id = idByKey.get(`${selection.productId}|${selection.date}`);
      return {
        ...(id ? { id } : {}),
        configurationItemId,
        merchantId: selection.merchantId,
        productId: selection.productId,
        date: selection.date,
        quantity: selection.quantity,
      };
    }),
  ];
};
